import React from 'react';
import { useTheme, THEMES } from '../contexts/ThemeContext';
import { Check, Palette, Sparkles } from 'lucide-react';
import { motion } from 'framer-motion';

export const ThemeSelector = () => {
  const { currentTheme, changeTheme, themes } = useTheme();
  
  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center" 
             style={{ background: 'var(--burnz-orange)' }}>
          <Palette className="w-5 h-5 text-white" />
        </div>
        <div>
          <h3 className="text-lg font-bold text-white">Theme Settings</h3>
          <p className="text-sm text-white/50">Customize the look and feel of your dashboard</p>
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {Object.values(themes).map((theme) => (
          <motion.button
            key={theme.id}
            onClick={() => changeTheme(theme.id)}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className={`relative group p-4 rounded-2xl border-2 transition-all text-left ${
              currentTheme === theme.id 
                ? 'border-[var(--burnz-orange)] bg-[var(--burnz-orange)]/10' 
                : 'border-white/10 bg-white/5 hover:border-white/20 hover:bg-white/10'
            }`}
          >
            {/* Preview gradient */}
            <div 
              className="w-full h-20 rounded-xl mb-3 relative overflow-hidden"
              style={{ background: theme.preview }}
            >
              {/* Animated pattern overlay */}
              <div className="absolute inset-0 opacity-30"
                   style={{
                     backgroundImage: `radial-gradient(circle at 30% 30%, ${theme.colors.primaryHex}40 0%, transparent 50%)`,
                   }} 
              />
              
              {/* Theme color dots */}
              <div className="absolute bottom-2 left-2 flex gap-1.5">
                <div 
                  className="w-4 h-4 rounded-full border border-white/20"
                  style={{ background: theme.colors.primaryHex }}
                />
                <div 
                  className="w-4 h-4 rounded-full border border-white/20"
                  style={{ background: theme.colors.secondaryHex }}
                />
                <div 
                  className="w-4 h-4 rounded-full border border-white/20"
                  style={{ background: theme.colors.surface2 }}
                />
              </div>
              
              {/* Selected check */}
              {currentTheme === theme.id && (
                <motion.div 
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className="absolute top-2 right-2 w-6 h-6 rounded-full flex items-center justify-center"
                  style={{ background: theme.colors.primaryHex }}
                >
                  <Check className="w-4 h-4 text-white" />
                </motion.div>
              )}
            </div>
            
            {/* Theme info */}
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <h4 className="font-semibold text-white">{theme.name}</h4>
                {theme.id === 'gaming' && (
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase"
                        style={{ background: theme.colors.primaryHex, color: 'white' }}>
                    New
                  </span>
                )}
              </div>
              <p className="text-xs text-white/50">{theme.description}</p>
            </div>
            
            {/* Active indicator */}
            {currentTheme === theme.id && (
              <div className="mt-3 flex items-center gap-1.5 text-xs font-medium"
                   style={{ color: theme.colors.primaryHex }}>
                <Sparkles className="w-3.5 h-3.5" />
                Currently Active
              </div>
            )}
          </motion.button>
        ))}
      </div>
      
      {/* Preview section */}
      <div className="mt-8 p-6 rounded-2xl border border-white/10 bg-white/5">
        <h4 className="text-sm font-semibold text-white mb-4">Preview Components</h4>
        
        <div className="flex flex-wrap gap-3">
          {/* Primary button */}
          <button 
            className="px-4 py-2 rounded-xl font-semibold text-white transition-all hover:opacity-90"
            style={{ background: 'var(--burnz-orange)' }}
          >
            Primary Button
          </button>
          
          {/* Secondary button */}
          <button 
            className="px-4 py-2 rounded-xl font-semibold text-white transition-all hover:opacity-90"
            style={{ background: 'var(--burnz-purple)' }}
          >
            Secondary Button
          </button>
          
          {/* Ghost button */}
          <button 
            className="px-4 py-2 rounded-xl font-semibold text-white border border-white/20 transition-all hover:bg-white/10"
          >
            Ghost Button
          </button>
          
          {/* Badge */}
          <span 
            className="px-3 py-1 rounded-full text-xs font-bold text-white"
            style={{ background: 'var(--burnz-orange)' }}
          >
            Badge
          </span>
          
          {/* Progress bar */}
          <div className="w-32 h-2 rounded-full overflow-hidden" style={{ background: 'var(--surface-3)' }}>
            <div 
              className="h-full rounded-full w-2/3"
              style={{ background: 'linear-gradient(90deg, var(--burnz-orange), var(--burnz-purple))' }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ThemeSelector;
